/**
 * **금액의 분모** — 「1인당 30만원」의 「인」.
 *
 * 실측에서 모집중 공고의 기업당 한도 2,392건 중 **231건(9.7%)**이 기업이 아닌
 * 무엇 하나당 금액인데 전부 「최대 N원」으로 나가고 있었다:
 *
 *     1인당 월 30만원 한도            화면 「최대 30만원」   12개월이면 360만원
 *     300만원/건당, 최대 2건          화면 「최대 300만원」  실제 600만원
 *     채용 청년 1인당 연 720만원      화면 「최대 720만원」  3명이면 2,160만원
 *
 * **모델이 못 읽어서가 아니다.** 둘 다 있으면 기업당을 제대로 고른다 —
 * 「1인당 500만 원(기업당 최대 1,500만 원)」에서 1,500만원을 보냈다. 분모가
 * 하나뿐일 때 넣을 칸이 없었을 뿐이다.
 *
 * ## 왜 enum 이 아닌가
 *
 * 분모 낱말을 목록 없이 그러모아 보니 인·건·과제·팀 밖에 이런 것들이 있었다:
 *
 *     개소(13) 회(13) 업소(10) 법인(6) 사업주(5) 작품(7) 제품(5) 박(6)
 *     프로그램(4) 컨소시엄(3) 척(3, 어선이다) 상점·점포(3) 가구 품목 실 …
 *
 * 25~30종이 더 있었고 새 공고마다 또 나온다(분모 출현의 12% 안팎). 닫힌
 * 목록으로 두면 그때마다 마이그레이션이 필요하고, 그 사이에는 화면이 조용히
 * 거짓말한다. **낱말은 그대로 담고 분류는 여기서 한다** — 사전이 코드에 있으면
 * 「가구당」이 새로 와도 화면은 이미 맞고, 필요할 때 한 줄 더하면 된다.
 */
/**
 * **신청자 자신을 가리키는 낱말.** 이건 분모가 아니라 기본값이다.
 *
 * 「기업당 최대 3,000만원」과 「최대 3,000만원」은 같은 말이라, 카드에
 * 「기업당」을 덧붙이면 없던 조건이 생긴 것처럼 읽힌다. 법인·사업주·업체는
 * 신청 주체의 다른 이름일 뿐이다.
 *
 * **장소는 여기 안 넣는다.** 처음엔 업소·개소·점포도 「한 곳만 가진 신청자가
 * 대부분」이라며 여기 뒀는데, 그건 이 작업이 없애려던 바로 그 거짓말이다 —
 * 「음식점 1개소당 최대 2,700만원」을 「최대 2,700만원」이라 쓰면 세 곳을
 * 가진 사람에게 3분의 1만 말한다. 「1개소당」이라고 쓰면 한 곳인 사람에게도
 * 여전히 맞다. 덜 말해서 안전한 쪽은 없다.
 */
const SAME_AS_APPLICANT = new Set([
    '기업', '업체', '사업자', '신청자', '신청기업', '개사', '사', '법인', '사업주',
]);
/**
 * 같은 뜻인데 어색하게 온 말. **모델을 고치는 게 아니라 화면을 고친다** —
 * 「횟수당」은 사람이 안 쓰는 말이고 「회당」이 맞다.
 *
 * 사전이 코드에 있어서 이런 건 한 줄이면 된다. 저장된 낱말은 원문 그대로 둔다.
 */
const ALIAS = { 횟수: '회', 개월: '월' };
/**
 * **기간 분모는 이번에 안 받는다.**
 *
 * 「1일당 1만원」은 기간을 알아야 총액이 나온다 — `durationMonths` 와 곱하는
 * 문제라 개수 분모와 성질이 다르고, 실측이 3건뿐이다. 분포를 모르는 채
 * 만들었다가 되돌린 적이 있어(`0020`→`0021`) 여기서는 비운다.
 */
const PERIOD = new Set(['일', '월', '년', '연', '연간', '주', '분기', '개월', '주간', '월간']);
/** 분모로 인정할 낱말인가. 한글 한두 낱말이어야 하고 기간은 아니어야 한다. */
export function isCountDenominator(word) {
    const w = word.trim();
    if (w.length === 0 || w.length > 6)
        return false;
    if (!/^[가-힣A-Za-z㎡]+$/.test(w))
        return false;
    return !PERIOD.has(w);
}
/**
 * 카드에 붙일 말. 「인」 → `1인당`, 「기업」 → `null`(그냥 「최대」로 간다).
 *
 * **「1」을 붙인다.** 「인당 30만원」보다 「1인당 30만원」이 읽기 자연스럽고
 * 공고문도 그렇게 쓴다. 두 글자 이상인 낱말은 안 붙인다 — 「1컨소시엄당」은
 * 어색하고 「컨소시엄당」이 맞다.
 */
export function amountPerLabel(amountPer) {
    if (!amountPer)
        return null;
    const raw = amountPer.trim();
    const w = ALIAS[raw] ?? raw;
    if (w.length === 0 || SAME_AS_APPLICANT.has(w) || PERIOD.has(w))
        return null;
    return w.length === 1 ? `1${w}당` : `${w}당`;
}
/* ------------------------------------------------------------ 기간 축 (`0029`) */
/**
 * **기간 축은 개수 축과 다른 칸이다.** 「1인당 월 30만원」은 둘 다 있다 —
 * 실측 32건이 그랬다. 한 칸에 넣으면 하나를 잃는다.
 *
 * 저장된 숫자 자체가 기간당 값인 것이 실측 69건(2.8%)이었다. 「월 60만원 ×
 * 최대 12개월」이 「최대 60만원」으로 나갔다.
 *
 * **곱하지 않는다.** 720만원은 원문에 없는 숫자고, `durationMonths` 가 있는
 * 공고도 4건뿐이다. 총액이 원문에 있으면 모델이 이미 그쪽을 고른다 —
 * 「월 50만원, 연간 300만원 한도」에서 300만원을 보냈다. 총액이 없을 때만
 * 기간당 값이 남고, 그때는 원문이 말한 대로 「월 30만원」이라고 말한다.
 */
/** 화면에 쓰는 말. 「연간」「년」은 「연」으로, 「매월」「개월」은 「월」로 모은다. */
const PERIOD_LABEL = {
    월: '월', 매월: '월', 개월: '월', 월간: '월',
    연: '연', 년: '연', 연간: '연', 매년: '연',
    일: '1일', 매일: '1일',
    주: '주', 주간: '주', 매주: '주',
    분기: '분기', 반기: '반기',
    시간: '시간당',
};
/** 기간 낱말로 받을 모양인가. 짧은 한글이면 된다 — 진짜 검증은 원문 대조가 한다. */
export function isPeriodWord(word) {
    const w = word.trim();
    return w.length > 0 && w.length <= 4 && /^[가-힣]+$/.test(w);
}
/**
 * 「월 30만원」의 「월」. 사전에 없는 낱말은 그대로 내보낸다 — 그게 text 로
 * 둔 이유다. 「반기당」이 와도 화면은 이미 맞다.
 */
export function amountPeriodLabel(period) {
    if (!period)
        return null;
    const w = period.trim();
    if (w.length === 0)
        return null;
    return PERIOD_LABEL[w] ?? w;
}
//# sourceMappingURL=amount-per.js.map